/**
 * Cross-message render plan for artifact tool parts.
 *
 * Mirrors the server-side render-mode resolver in `@taqat/ai/artifacts` but
 * lives in the client so the chat thread can compute the plan during render
 * without pulling in any server dependencies.
 *
 * Decisions:
 *   - `preview` — render the full DocumentPreview card. Latest create per
 *     artifact only.
 *   - `badge`   — render the compact ArtifactUpdateBadge. Every update plus
 *     superseded creates.
 *   - `hidden`  — render nothing. Reserved for back-to-back duplicate update
 *     calls within ONE assistant message (model retries).
 *
 * @module lib/artifacts/render-modes
 */

import type { UIMessage } from "ai";
import { ARTIFACT_TOOL_NAMES } from "@taqat/core/types";

/** Tool names that participate in render-mode dedupe. */
const ARTIFACT_TOOL_NAME_SET: ReadonlySet<string> = new Set([
  ARTIFACT_TOOL_NAMES.CREATE,
  ARTIFACT_TOOL_NAMES.UPDATE,
  ARTIFACT_TOOL_NAMES.GENERATE_IMAGE,
]);

/** Render decision for one artifact tool part. */
export type ArtifactRenderMode = "preview" | "badge" | "hidden";

/** Per-message render plan. Maps part index → mode. */
export type MessageArtifactRenderPlan = Map<number, ArtifactRenderMode>;

interface ArtifactPartLocation {
  messageId: string;
  partIdx: number;
}

interface MutableToolPart {
  type?: string;
  state?: string;
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
}

/**
 * Walks every assistant message and returns a `messageId → plan` map.
 * Pure function — safe to call in `useMemo` on every render.
 */
export function computeArtifactRenderModes(
  messages: UIMessage[],
): Map<string, MessageArtifactRenderPlan> {
  type PartRef = ArtifactPartLocation & { op: "create" | "update" };
  const groups = new Map<string, { creates: PartRef[]; updates: PartRef[] }>();

  for (const message of messages) {
    if (message.role !== "assistant") continue;
    const messageId = message.id;
    const parts = (message.parts ?? []) as MutableToolPart[];
    for (let idx = 0; idx < parts.length; idx++) {
      const part = parts[idx];
      if (!part || typeof part.type !== "string" || !part.type.startsWith("tool-")) continue;
      const toolName = part.type.slice("tool-".length);
      if (!ARTIFACT_TOOL_NAME_SET.has(toolName)) continue;

      const key = artifactKey(part);
      if (!key) continue;

      const op: "create" | "update" =
        toolName === ARTIFACT_TOOL_NAMES.UPDATE ? "update" : "create";
      const ref: PartRef = { messageId, partIdx: idx, op };
      const bucket = groups.get(key) ?? { creates: [], updates: [] };
      if (op === "create") bucket.creates.push(ref);
      else bucket.updates.push(ref);
      groups.set(key, bucket);
    }
  }

  const plans = new Map<string, MessageArtifactRenderPlan>();
  const setMode = (loc: ArtifactPartLocation, mode: ArtifactRenderMode) => {
    let plan = plans.get(loc.messageId);
    if (!plan) {
      plan = new Map();
      plans.set(loc.messageId, plan);
    }
    plan.set(loc.partIdx, mode);
  };

  for (const { creates, updates } of groups.values()) {
    if (creates.length > 0) {
      const last = creates[creates.length - 1];
      for (const ref of creates) {
        setMode(ref, ref === last ? "preview" : "badge");
      }
    }

    // Updates: every one renders as a badge across messages. Within ONE
    // message, collapse model-retry duplicates to the latest only.
    const lastUpdateInMessage = new Map<string, PartRef>();
    for (const ref of updates) {
      lastUpdateInMessage.set(ref.messageId, ref);
    }
    for (const ref of updates) {
      const winner = lastUpdateInMessage.get(ref.messageId);
      setMode(ref, winner === ref ? "badge" : "hidden");
    }
  }

  return plans;
}

/**
 * Stable identity key for an artifact tool part — id-first, falls back to
 * normalized title so streaming-but-not-yet-finished tools still group.
 */
function artifactKey(part: MutableToolPart): string {
  const output = (part.output ?? {}) as { id?: unknown; title?: unknown };
  const input = (part.input ?? {}) as { title?: unknown };
  const id = typeof output.id === "string" ? output.id.trim() : "";
  if (id) return `id:${id}`;

  const title =
    (typeof output.title === "string" && output.title) ||
    (typeof input.title === "string" && input.title) ||
    "";
  if (!title) return "";
  return `t:${title.trim().toLowerCase()}`;
}
